import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthCard } from '@/components/auth/auth-card';
import { AuthField } from '@/components/auth/auth-field';
import { AuthForm } from '@/components/auth/auth-form';
import { signInFn } from '@/server/functions/auth';

const searchSchema = z.object({
    redirect: z.string().optional(),
});

export const Route = createFileRoute('/_auth/sign-in')({
    component: SignInPage,
    validateSearch: searchSchema,
});

const signInSchema = z.object({
    email: z.email('נא להזין כתובת אימייל תקינה'),
    password: z.string().min(1, 'נא להזין סיסמה'),
});

function SignInPage() {
    const search = useSearch({ from: '/_auth/sign-in' });
    const redirectTo = search.redirect || '/admin';
    const form = useForm({
        resolver: zodResolver(signInSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const signInMutation = useMutation({
        mutationFn: async (data: z.infer<typeof signInSchema>) => {
            await signInFn({
                data: {
                    email: data.email,
                    password: data.password,
                    redirect: redirectTo,
                },
            });
        },
        onError: (error: Error) => {
            form.setError('root', { message: error.message });
        },
    });

    return (
        <AuthCard
            title="התחברות"
            description="הגישה למערכת הניהול שמורה למנהלים."
            footer={
                <span className="flex flex-col gap-2 sm:flex-row sm:justify-center sm:gap-4">
                    <Link
                        to="/recover"
                        className="!no-underline hover:underline"
                    >
                        שכחתם סיסמה?
                    </Link>
                    <Link
                        to="/sign-up"
                        className="!no-underline hover:underline"
                    >
                        הרשמה לחשבון חדש
                    </Link>
                </span>
            }
        >
            <AuthForm
                schema={signInSchema}
                defaultValues={{ email: '', password: '' }}
                onSubmit={(data) => signInMutation.mutate(data)}
                submitText="התחברות"
                isLoading={signInMutation.isPending}
                form={form}
            >
                {(f) => (
                    <>
                        <AuthField
                            control={f.control}
                            name="email"
                            label="אימייל"
                            placeholder="editor@ashkelon.news"
                            type="email"
                        />
                        <AuthField
                            control={f.control}
                            name="password"
                            label="סיסמה"
                            placeholder="הזינו סיסמה"
                            type="password"
                        />
                    </>
                )}
            </AuthForm>
        </AuthCard>
    );
}
